/**
 * Utility to manage authentication cookies on the shared domain.
 *
 * The middleware gateway authenticates via cookies:
 *   accessToken, idToken, refreshToken, userPoolId, clientId, region
 *
 * Because the project uses a custom AuthService (not Amplify Auth.signIn()),
 * Amplify's cookieStorage does NOT automatically write these cookies.
 * We set them manually after login / token refresh, and clear on logout.
 *
 * The cookie domain comes from REACT_APP_AUTH_COOKIE_DOMAIN.
 * In DEV it should be ".dev-dttsynergy.com" (shared parent domain)
 * so that cookies are sent cross-subdomain to the middleware.
 */

import {
  AUTH_COOKIE_DOMAIN,
  AUTH_USER_POOL_ID,
  AUTH_USER_POOL_WEB_CLIENT_ID,
  AUTH_REGION,
} from './config/configs';

const COOKIE_DOMAIN = AUTH_COOKIE_DOMAIN || '';
const COOKIE_PATH = '/';
const COOKIE_MAX_AGE = 60 * 60 * 24; // 24 hours (seconds)

/**
 * Set a single cookie.
 */
const setCookie = (name, value, maxAge = COOKIE_MAX_AGE) => {
  if (!value) return;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `path=${COOKIE_PATH}`,
    `max-age=${maxAge}`,
    'SameSite=None',
    'Secure',
  ];
  if (COOKIE_DOMAIN) {
    parts.push(`domain=${COOKIE_DOMAIN}`);
  }
  document.cookie = parts.join('; ');
};

/**
 * Remove a single cookie by setting max-age=0.
 */
const removeCookie = (name) => {
  const parts = [
    `${name}=`,
    `path=${COOKIE_PATH}`,
    'max-age=0',
    'SameSite=None',
    'Secure',
  ];
  if (COOKIE_DOMAIN) {
    parts.push(`domain=${COOKIE_DOMAIN}`);
  }
  document.cookie = parts.join('; ');
};

/**
 * Write all auth cookies the middleware expects.
 *
 * @param {string} accessToken  - Cognito AccessToken
 * @param {string} idToken      - Cognito IdToken
 * @param {string} refreshToken - Cognito RefreshToken
 */
export const setAuthCookies = (accessToken, idToken, refreshToken) => {
  setCookie('accessToken', accessToken);
  setCookie('idToken', idToken);
  setCookie('refreshToken', refreshToken);
  setCookie('userPoolId', AUTH_USER_POOL_ID);
  setCookie('clientId', AUTH_USER_POOL_WEB_CLIENT_ID);
  setCookie('region', AUTH_REGION || 'eu-west-1');
};

/**
 * Remove all auth cookies (logout).
 */
export const clearAuthCookies = () => {
  removeCookie('accessToken');
  removeCookie('idToken');
  removeCookie('refreshToken');
  removeCookie('userPoolId');
  removeCookie('clientId');
  removeCookie('region');
};
